import { useEffect, useRef, useState } from 'react';
import {
  parsePagerFeed,
  suggestedIncidentName,
  haversineMeters,
  type ParsedPagerEvent,
  type AiPagerExtraction,
  type IncidentType,
  type LngLat,
} from '@duty/shared';
import { MAPBOX_TOKEN } from '@/config/mapbox';

/**
 * Real-time pager-message parsing for the landing-page paste box.
 *
 * Any message, in any format, should resolve to *something* usable:
 *  1. Try the deterministic LGDO-format parser first (free, instant).
 *  2. If that doesn't look like a well-formed pager line, ask the API's
 *     AI-assist endpoint to have a go (only when it's actually needed —
 *     never for a message the deterministic parser already handled).
 *  3. If there's no usable address text but there IS a coordinate, fall
 *     back to the coordinate itself as the location.
 *  4. If there's both an address and a coordinate, forward-geocode the
 *     address and flag a discrepancy if it lands far from the paged
 *     coordinate — the coordinate is often where the *call* was made
 *     (e.g. a driveway/paddock GPS ping), not the addressed entrance the
 *     text describes.
 *
 * Every step degrades honestly (never fabricates a field) and the result is
 * always presented for the officer to review, never auto-submitted.
 */

export interface PagerParsePreview {
  status: 'idle' | 'parsing' | 'ai-pending' | 'done';
  /** The deterministic (or AI-assisted) event actually used. */
  event: ParsedPagerEvent | null;
  aiExtracted: boolean;
  aiUnavailableReason?: string;
  /** Location text to show/apply — falls back to the coordinate string when no address text exists. */
  effectiveLocation: string;
  effectiveCoordinates: LngLat | undefined;
  effectiveIncidentType: IncidentType;
  suggestedName: string;
  /** Metres between the paged coordinate and the geocoded address, when both are known. */
  geocodeDiscrepancyM?: number;
  geocodeChecking: boolean;
}

const DISCREPANCY_THRESHOLD_M = 400;
const GEOCODE_DEBOUNCE_MS = 400;
const PARSE_DEBOUNCE_MS = 250;

function coordString(point: LngLat): string {
  return `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;
}

async function geocodeAddress(address: string): Promise<LngLat | undefined> {
  if (!MAPBOX_TOKEN) return undefined;
  try {
    const url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json` +
      `?access_token=${MAPBOX_TOKEN}&country=au,nz&limit=1&language=en`;
    const res = await fetch(url);
    if (!res.ok) return undefined;
    const data = (await res.json()) as { features?: { center?: [number, number] }[] };
    const center = data.features?.[0]?.center;
    return center ? { lng: center[0], lat: center[1] } : undefined;
  } catch {
    return undefined;
  }
}

interface AiExtractResponse {
  aiUsed: boolean;
  extraction?: AiPagerExtraction;
  reason?: string;
}

async function fetchAiExtraction(text: string): Promise<AiExtractResponse> {
  try {
    const res = await fetch('/api/pager-feed/extract', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (!res.ok) return { aiUsed: false, reason: `API returned ${res.status}` };
    return (await res.json()) as AiExtractResponse;
  } catch {
    return { aiUsed: false, reason: 'API unreachable' };
  }
}

const IDLE: PagerParsePreview = {
  status: 'idle',
  event: null,
  aiExtracted: false,
  effectiveLocation: '',
  effectiveCoordinates: undefined,
  effectiveIncidentType: 'Other',
  suggestedName: '',
  geocodeChecking: false,
};

export function usePagerParse(text: string): PagerParsePreview {
  const [preview, setPreview] = useState<PagerParsePreview>(IDLE);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const trimmed = text.trim();
    if (!trimmed) {
      setPreview(IDLE);
      return;
    }

    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestIdRef.current === requestId;

    const timer = window.setTimeout(async () => {
      const [deterministic] = parsePagerFeed(trimmed);
      const looksUnparsed =
        !deterministic ||
        (!deterministic.wellFormed && !deterministic.location && !deterministic.coordinates);

      let event = deterministic ?? null;
      let aiExtracted = false;
      let aiUnavailableReason: string | undefined;

      if (looksUnparsed) {
        if (!isCurrent()) return;
        setPreview((p) => ({ ...p, status: 'ai-pending' }));
        const result = await fetchAiExtraction(trimmed);
        if (!isCurrent()) return;
        if (result.aiUsed && result.extraction) {
          event = {
            raw: trimmed,
            typeText: result.extraction.typeText,
            location: result.extraction.location,
            coordinates: result.extraction.coordinates,
            incidentNumber: result.extraction.incidentNumber,
            incidentType: result.extraction.incidentType,
            wellFormed: false,
          } as ParsedPagerEvent;
          aiExtracted = true;
        } else {
          aiUnavailableReason = result.reason;
        }
      }

      const effectiveIncidentType = event?.incidentType ?? 'Other';
      const addressText = event?.expandedLocation ?? event?.location;
      const effectiveCoordinates = event?.coordinates;
      const effectiveLocation = addressText ?? (effectiveCoordinates ? coordString(effectiveCoordinates) : '');
      const suggestedName = event ? suggestedIncidentName(event) : '';

      if (!isCurrent()) return;
      setPreview({
        status: 'done',
        event,
        aiExtracted,
        aiUnavailableReason,
        effectiveLocation,
        effectiveCoordinates,
        effectiveIncidentType,
        suggestedName,
        geocodeChecking: Boolean(addressText && effectiveCoordinates),
      });

      // Discrepancy check: only meaningful when we have BOTH real address text
      // and a paged coordinate to compare it against.
      if (addressText && effectiveCoordinates) {
        window.setTimeout(async () => {
          if (!isCurrent()) return;
          const geocoded = await geocodeAddress(addressText);
          if (!isCurrent() || !geocoded) {
            if (isCurrent()) setPreview((p) => ({ ...p, geocodeChecking: false }));
            return;
          }
          const distanceM = haversineMeters(geocoded, effectiveCoordinates);
          setPreview((p) => ({
            ...p,
            geocodeChecking: false,
            geocodeDiscrepancyM: distanceM >= DISCREPANCY_THRESHOLD_M ? Math.round(distanceM) : undefined,
          }));
        }, GEOCODE_DEBOUNCE_MS);
      }
    }, PARSE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [text]);

  return preview;
}
